/**
 * "Is this wait worth drawing anything about?" — one implementation, because a second one
 * drifts and because the answer is the same everywhere.
 *
 * Under the delay, nothing is drawn at all. The common case by far is a query that resolves
 * almost at once — a warm cache, a local API, a signed-out person opening a bookmark whose
 * guard redirects — and a loader drawn for one frame in between is a flash of a screen nobody
 * was meant to read. Over it, the wait is long enough that silence would read as a broken
 * page, so the loader appears and stays until there is an answer.
 *
 * Lifted out of `features/session/RequireSession`, which had it inline as `STATUS_DELAY_MS`
 * and a `waitIsLong` state, when the page loader landed (CLAUDE.md "Loading states",
 * 2026-09-12): the same "draw nothing under 200 ms" belongs in front of every page loader,
 * not just the guard's, and retro rule 8 says a cross-cutting rule lands as one object the
 * first time it appears twice.
 *
 * It says nothing about *what* to draw once the wait is long. A screen that knows its layout
 * draws a `Skeleton` immediately and never asks this question; this is the gate in front of
 * `PageLoader`, which is for the screens that know nothing yet.
 *
 * The one exception to "draw nothing under 200 ms" is a wait that was already being drawn when
 * this code arrived — the boot loader in `index.html` (`lib/boot.ts`). That is what
 * `initiallyLong` is for: the delay exists to avoid a flash, and there is nothing to flash
 * when the loader is on screen already.
 */

import { useEffect, useState } from 'react';

/** The delay the guard has used since the entrance wave, and the default for everything else. */
export const LONG_WAIT_MS = 200;

export interface LongWaitOptions {
  /**
   * Start as if the delay had already passed. Only the caller that takes over from the boot
   * loader passes it, and only on a cold start — see `lib/boot.ts`. It seeds the state and
   * nothing more: `useState`'s initialiser runs once, so the first time the wait ends this
   * stops mattering and every later wait behaves normally.
   */
  readonly initiallyLong?: boolean;
}

export function useLongWait(
  pending: boolean,
  delayMs: number = LONG_WAIT_MS,
  options?: LongWaitOptions,
): boolean {
  const [isLong, setIsLong] = useState(options?.initiallyLong ?? false);

  useEffect(() => {
    if (!pending) {
      setIsLong(false);
      return;
    }
    const timer = window.setTimeout(() => {
      setIsLong(true);
    }, delayMs);
    return () => {
      window.clearTimeout(timer);
    };
  }, [pending, delayMs]);

  return isLong;
}
